import React from 'react';

/**
 * `useLayoutEffect` no cliente, `useEffect` no servidor.
 *
 * Medir só faz sentido depois do layout, e no SSR o React avisa que
 * `useLayoutEffect` não roda — a troca cala o aviso sem mudar o comportamento
 * no navegador.
 */
const useLayoutEffectSeguro =
  typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

/**
 * Altura, em pixels, que o painel precisa ter para mostrar exatamente as
 * `visiveis` primeiras linhas — nem uma fatia da seguinte.
 *
 * Não dá para cravar essa altura no CSS: a linha ocupa uma ou duas linhas de
 * texto conforme a largura da coluna e o tamanho do e-mail/telefone (uma
 * unidade com dois números quebra antes das outras), então o mesmo painel tem
 * alturas diferentes na mesma página. Medir é o único jeito de acertar sempre.
 *
 * Devolve `undefined` enquanto não há o que limitar (menos itens que o teto,
 * ou antes da hidratação): aí vale o `max-height` de CSS, que é a aproximação
 * usada por quem está sem JavaScript.
 */
export const useAlturaDeLinhas = (
  ref: React.RefObject<HTMLElement | null>,
  visiveis: number,
  // Remedir quando a lista filtrada muda; o conteúdo novo tem outras alturas.
  deps: unknown[],
) => {
  const [altura, setAltura] = React.useState<number>();

  useLayoutEffectSeguro(() => {
    const lista = ref.current;
    if (!lista) return undefined;

    const medir = () => {
      const linhas = Array.from(lista.children) as HTMLElement[];

      if (linhas.length <= visiveis) {
        setAltura(undefined);
        return;
      }

      // `offsetTop` é posição de layout (não muda com a rolagem) e as duas
      // linhas compartilham o mesmo `offsetParent`, então a diferença já é a
      // altura acumulada — independente de onde o painel esteja na página.
      const ultima = linhas[visiveis - 1];
      setAltura(ultima.offsetTop + ultima.offsetHeight - linhas[0].offsetTop);
    };

    medir();

    if (typeof ResizeObserver === 'undefined') return undefined;

    // Observa o PAI: a largura dele não muda quando a barra de rolagem do
    // painel aparece, então a medição não entra em vaivém.
    const alvo = lista.parentElement ?? lista;
    const observer = new ResizeObserver(medir);
    observer.observe(alvo);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, visiveis, ...deps]);

  return altura;
};

export default useAlturaDeLinhas;
